import React from "react";
import { useTheme } from "@mui/material";
import { ErrorBox, Section } from "../organisation/OrgModal";
import CurrentPlanCard from "../subscription/CurrentPlanCard";
import OrgHistoryPanel from "../subscription/OrgHistoryPanel";
import usePublicPlans from "../../hooks/usePublicPlans";
import { OwnedAddonRow } from "../subscription/AddonsSection";
import { apiErrorMessage, planOf, useAdminOrgPlan } from "../../utils/subscriptionData";

// Any organisation's plan, read-only: the plan card, its add-ons, and its plan and payment history.
const OrgPlanReadOnly = ({ org }) => {
  const { data, loading, error } = useAdminOrgPlan(org.id);
  const { plans } = usePublicPlans();
  const addons = (data?.addons || []).filter((addon) => addon.status === "active" || addon.status === "canceling");
  const publicPlan = plans.find((item) => item.plan_id === planOf(data?.plan)?.plan_id);

  return (
    <div className="h-full overflow-auto">
      <div className="flex w-full max-w-6xl flex-col gap-3 p-4">
        {error ? (
          <ErrorBox>{apiErrorMessage(error, `Could not load ${org.name}'s plan.`)}</ErrorBox>
        ) : (
          <CurrentPlanCard plan={data?.plan} addons={data?.addons} plans={plans} loading={loading || !data} />
        )}

        {addons.length > 0 && (
          <Section dense title={`Add-ons (${addons.length})`} description="Extras on top of the plan's allowances.">
            <ul className="-my-2 divide-y">
              {addons.map((addon) => (
                <OwnedAddonRow key={addon.id} addon={addon} unitCents={publicPlan?.addons?.[addon.addon_type]?.amount_cents} />
              ))}
            </ul>
          </Section>
        )}

        {data && !error && <OrgHistoryPanel orgId={org.id} history={data.history} />}
      </div>
    </div>
  );
};

// Plan & billing for a Super Admin. Inside an organisation, their own or any other, they get that organisation's
// full billing page (`own`) with every action: buy, upgrade, add-ons, Manage Plan. Checkout and the billing portal
// act on the organisation in the sign-in token, so the page always belongs to the organisation they are inside.
// Outside every organisation there is nothing to act on, so their own organisation's plan is shown read-only.
const SuperAdminPlan = ({ home, tokenOrgId, own }) => {
  const theme = useTheme();
  if (!tokenOrgId && !home) return null;
  return (
    <div className="h-full w-full" style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}>
      {tokenOrgId ? own : <OrgPlanReadOnly org={home} />}
    </div>
  );
};

export default SuperAdminPlan;
